import { Injectable } from '@angular/core';
import {
  CompanyStatusEnum,
  GstStatusEnum,
  KycStatusEnum,
} from '@generated/graphql';
import { replace, startCase } from 'lodash-es';
import { AppStatusType } from '@shared/component/common/status/status.component';
import { Company } from '@app/modules/company/models/company.model';
import { DateTime } from 'luxon';
import {parseAnyDate} from "@shared/helpers/date-time.helpers";

@Injectable({
  providedIn: 'root',
})
export class CompanyHelper {
  private static instance: CompanyHelper;

  public static getInstance(): CompanyHelper {
    if (!CompanyHelper.instance) {
      this.instance = new CompanyHelper();
    }
    return CompanyHelper.instance;
  }

  public renderCompanyStatusLabel(status: CompanyStatusEnum) {
    return status ? startCase(status.toLowerCase()) : null;
  }

  public renderCompanyStatusStyle(status: CompanyStatusEnum): AppStatusType {
    switch (status) {
      case CompanyStatusEnum.Active:
        return 'success';
      case CompanyStatusEnum.InProcess:
        return 'new-light';
      case CompanyStatusEnum.Dormant:
        return 'warn';
      case CompanyStatusEnum.Archived:
        return 'error';
      default:
        return 'default';
    }
  }

  public renderKYCStatusLabel(status: KycStatusEnum) {
    return status
      ? replace(startCase(status.toLowerCase()), 'Kyc', '')
      : null;
  }

    public renderKYCStatusLabelNew(status: KycStatusEnum): AppStatusType {
    switch (status) {
      case KycStatusEnum.KycIncomplete:
        return 'Incomplete';
      case KycStatusEnum.KycSubmitted:
        return 'Verifying';
      case KycStatusEnum.KycVerifying:
        return 'Verifying';
      default:
        return 'Confirmed'
    }
  }

  public renderKYCStatusStyle(status: KycStatusEnum): AppStatusType {
    switch (status) {
      case KycStatusEnum.KycIncomplete:
        return 'hold';
      case KycStatusEnum.KycConfirmed:
        return 'success';
      case KycStatusEnum.KycVerifying:
        return 'awaiting';
      case KycStatusEnum.KycSubmitted:
        return 'action-light';
      default:
        return 'default';
    }
  }

  public renderKYCStatusStepStyle(status: KycStatusEnum, kycStatus: string): AppStatusType {
    const styleMap: Record<KycStatusEnum, Record<string, AppStatusType>> = {
      [KycStatusEnum.KycIncomplete]: {
        [KycStatusEnum.KycIncomplete]: 'status-default',
        [KycStatusEnum.KycVerifying]: 'default',
        [KycStatusEnum.KycConfirmed]: 'default',
      },
      [KycStatusEnum.KycVerifying]: {
        [KycStatusEnum.KycIncomplete]: 'default',
        [KycStatusEnum.KycVerifying]: 'status-action-light',
        [KycStatusEnum.KycConfirmed]: 'default',
      },
      [KycStatusEnum.KycSubmitted]: {
        [KycStatusEnum.KycIncomplete]: 'default',
        [KycStatusEnum.KycVerifying]: 'status-action-light',
        [KycStatusEnum.KycConfirmed]: 'default',
      },
      [KycStatusEnum.KycConfirmed]: {      
         [KycStatusEnum.KycIncomplete]: 'default',
        [KycStatusEnum.KycVerifying]: 'default',
        [KycStatusEnum.KycConfirmed]: 'status-success',
      },
    };

    return styleMap[status]?.[kycStatus] ?? 'default';
  }

  public renderGSTStatusLabel(status: GstStatusEnum): string {
    return status ? startCase(status.toLowerCase()) : null;
  }

  public renderGSTStatusStyle(status: GstStatusEnum): AppStatusType {
    switch (status) {
      case GstStatusEnum.Unregistered:
        return 'error';
      case GstStatusEnum.Registered:
        return 'success';
      default:
        return 'default';
    }
  }

  public generateYearPeriod(incorporateYear: Date) {
    const currentYear = DateTime.now().year;
    if (incorporateYear) {
      const incorporatedYear = parseAnyDate(incorporateYear).getFullYear();
      return Array.from(
        { length: currentYear - incorporatedYear + 1 },
        (_, i) => currentYear - i,
      );
    }
    return [currentYear];
  }

  public getAGMDeadline(company: Company) {
    if (company.status === CompanyStatusEnum.InProcess) {
      return null;
    }

    if (company.country.code === 'SG') {
      return this.getSingaporeAGMDeadline(company);
    } else if (company.country.code === 'HK') {
      return this.getHongKongAGMDeadline(company);
    }

    return null;
  }

  public getAnnualReturnDeadline(company: Company) {
    if (company.status === CompanyStatusEnum.InProcess) {
      return null;
    }

    if (company.country.code === 'SG') {
      const agmDeadline = this.getSingaporeAGMDeadline(company);

      return this.getSingaporeAnnualReturnDeadline(agmDeadline);
    } else if (company.country.code === 'HK') {
      return this.getHongKongAnnualReturnDeadline(company);
    }

    return null;
  }

  private getSingaporeAGMDeadline(company: Company) {
    let agmDeadline = DateTime.fromJSDate(company.getNextFYEValue()).plus({
      months: 6,
    });

    return agmDeadline.toJSDate();
  }

  private getSingaporeAnnualReturnDeadline(agmDeadline: Date) {
    const annualReturn = DateTime.fromJSDate(agmDeadline).plus({ months: 1 });

    return annualReturn.toJSDate();
  }

  private getHongKongAGMDeadline(company: Company) {
    const agmDeadline = DateTime.fromJSDate(company.getNextFYEValue()).plus({
      months: 6,
    });

    return agmDeadline.toJSDate();
  }

  private getHongKongAnnualReturnDeadline(company: Company) {
    let annualReturnDeadline = DateTime.now();

    const registrationDate = DateTime.fromFormat(
      company.registrationDate.toString(),
      'yyyy-MM-dd HH:mm:ss',
    );

    annualReturnDeadline = annualReturnDeadline.set({
      day: registrationDate.day,
      month: registrationDate.month,
    });

    annualReturnDeadline = annualReturnDeadline.plus({ days: 42 });

    return annualReturnDeadline.toJSDate();
  }

  public getECIDeadline(company: Company) {
    if (company.status === CompanyStatusEnum.InProcess) {
      return null;
    }

    let eciDeadline = DateTime.fromJSDate(company.getNextFYEValue()).plus({
      months: 3,
    });

    return eciDeadline.toJSDate();
  }

  public getTaxReturnDeadline(company: Company) {
    if (company.status === CompanyStatusEnum.InProcess) {
      return null;
    }

    if (company.country.code === 'SG') {
      return this.getSingaporeTaxReturnDeadline(company);
    } else if (company.country.code === 'HK') {
      return this.getHongKongTaxReturnDeadline(company);
    }

    return null;
  }

  private getSingaporeTaxReturnDeadline(company: Company) {
    let taxReturnDeadline = DateTime.fromJSDate(company.getNextFYEValue())
      .set({ month: 11 })
      .endOf('month')
      .plus({ year: 1 });

    return taxReturnDeadline.toJSDate();
  }

  private getHongKongTaxReturnDeadline(company: Company) {
    const fyeDate = DateTime.fromJSDate(company.getNextFYEValue());

    let taxReturnDate: DateTime;

    // Case #1
    //1 January to 31 March
    // 15 August following year
    const fromDateCase1 = DateTime.now()
      .set({ month: 1, year: fyeDate.year })
      .startOf('month');
    const toDateCase1 = DateTime.now()
      .set({ month: 3, year: fyeDate.year })
      .endOf('month');

    // Case #2
    //1 April to 30 November
    // tax return: 2 May following year
    const fromDateCase2 = DateTime.now()
      .set({ month: 4, year: fyeDate.year })
      .startOf('month');
    const toDateCase2 = DateTime.now()
      .set({ month: 11, year: fyeDate.year })
      .endOf('month');

    // Case #3
    //1 December to 31 December
    // 15 August following year
    // const fromDateCase3 = DateTime.now()
    //   .set({ month: 12, year: fyeDate.year })
    //   .startOf('month');
    // const toDateCase3 = DateTime.now()
    //   .set({ month: 12, year: fyeDate.year })
    //   .endOf('month');

    if (fyeDate >= fromDateCase1 && fyeDate <= toDateCase1) {
      taxReturnDate = DateTime.fromObject({
        day: 15,
        month: 11,
        year: DateTime.now().year,
      });
    } else if (fyeDate >= fromDateCase2 && fyeDate <= toDateCase2) {
      taxReturnDate = DateTime.fromObject({
        day: 2,
        month: 5,
        year: fyeDate.year + 1,
      });
    } else {
      taxReturnDate = DateTime.fromObject({
        day: 15,
        month: 8,
        year: fyeDate.year + 1,
      });
    }

    return taxReturnDate;
  }
}
